import { fetchJSON as fetchAPI } from './client';

export type RegisteredPrompt = {
  id: string;
  identifier: string;
  version: string;
  purpose: string;
  owner: string;
  variables?: string[];
  guardrails?: string[];
  evaluation_score?: number;
  lifecycle: string;
  usage_count_24h: number;
};

export type RegisteredModel = {
  id: string;
  name: string;
  provider: string;
  capabilities?: string[];
  context_window: number;
  avg_latency_ms: number;
  cost_per_1k_tokens: number;
  availability: string;
  lifecycle: string;
  fallback_model?: string;
};

export type RegisteredAgent = {
  id: string;
  name: string;
  kind: string;
  mission: string;
  responsibilities?: string[];
  tools?: string[];
  memory_scope: string;
  lifecycle: string;
};

export type RegisteredTool = {
  id: string;
  name: string;
  kind: string;
  description: string;
  permissions?: string[];
  rate_limit_rps: number;
  timeout_ms: number;
  owner: string;
  version: string;
};

export type MemoryCatalogEntry = {
  id: string;
  kind: string;
  name: string;
  scope: string;
  policy: string;
  entry_count: number;
  size_bytes: number;
  tenant_isolated: boolean;
};

export type RoutingPolicy = {
  id: string;
  name: string;
  strategy: string;
  primary_model: string;
  fallback_model?: string;
  cost_optimized: boolean;
  latency_optimized: boolean;
  enabled: boolean;
};

export type InferenceRequest = {
  model_id?: string;
  prompt_id?: string;
  input: string;
  use_memory?: boolean;
  use_context?: boolean;
};

export type InferenceResult = {
  execution_id: string;
  model_used: string;
  provider: string;
  output: string;
  tokens_used: number;
  latency_ms: number;
  cost_usd: number;
  fallback_used: boolean;
};

export type BuiltContext = {
  tenant_id: string;
  semantic_context?: string[];
  business_definitions?: string[];
  kpi_context?: string[];
  policy_context?: string[];
  memory_snippets?: string[];
  context_quality_score: number;
  generated_at: string;
};

export type AIPlatformHealth = {
  ai_platform_health_score: number;
  token_usage_24h: number;
  prompt_success_rate: number;
  avg_inference_latency_ms: number;
  model_cost_24h_usd: number;
  memory_utilization_pct: number;
  context_quality_avg: number;
  registered_prompts: number;
  registered_models: number;
  registered_agents: number;
  registered_tools: number;
};

export type AIPlatformDashboard = {
  health: AIPlatformHealth;
  prompts: number;
  models: number;
  agents: number;
  tools: number;
  memory_stores: number;
  routing_policies: number;
  extension_points: { dev_id: string; name: string; description: string }[];
};

const AI_BASE = import.meta.env.VITE_AI_PLATFORM_API ?? '/api/v1/ai';

function fetchAI<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(`${AI_BASE}${path}`, init);
}

export const aiPlatformGovernanceApi = {
  listPrompts: () => fetchAI<{ prompts: RegisteredPrompt[] }>('/prompts/registry').then((r) => r.prompts),
  listModels: () => fetchAI<{ models: RegisteredModel[] }>('/models/registry').then((r) => r.models),
  listAgents: () => fetchAI<{ agents: RegisteredAgent[] }>('/agents/registry').then((r) => r.agents),
  listTools: () => fetchAI<{ tools: RegisteredTool[] }>('/tools/registry').then((r) => r.tools),
  listMemory: () => fetchAI<{ memory: MemoryCatalogEntry[] }>('/memory').then((r) => r.memory),
  listRouting: () => fetchAI<{ routing_policies: RoutingPolicy[] }>('/routing').then((r) => r.routing_policies),
  runInference: (body: InferenceRequest) =>
    fetchAI<{ data: InferenceResult }>('/inference', { method: 'POST', body: JSON.stringify(body) }).then((r) => r.data),
  buildContext: (body: { include_semantic?: boolean; include_memory?: boolean; include_policy?: boolean; entity?: string }) =>
    fetchAI<BuiltContext>('/context', { method: 'POST', body: JSON.stringify(body) }),
  health: () => fetchAI<AIPlatformHealth>('/health'),
  dashboard: () => fetchAI<AIPlatformDashboard>('/dashboard'),
};
